import { type Breadcrumb, breadcrumbSchema } from "@/lib/seo";

interface BreadcrumbsProps {
  items: Breadcrumb[];
  className?: string;
  /** Set false where the route's head() already emits BreadcrumbList JSON-LD. */
  emitSchema?: boolean;
}

/**
 * Renders a visible breadcrumb trail and, unless the route already does it in
 * head(), emits the BreadcrumbList JSON-LD. Exactly one of the two should emit.
 * The final crumb is the current page, rendered as plain text, not a link.
 */
export function Breadcrumbs({ items, className, emitSchema = true }: BreadcrumbsProps) {
  if (items.length === 0) return null;
  return (
    <nav
      aria-label="Breadcrumb"
      className={className ?? "mx-auto max-w-7xl px-6 pt-6 text-xs text-muted-foreground lg:px-8"}
    >
      <ol className="flex flex-wrap items-center gap-1.5">
        {items.map((b, i) => {
          const last = i === items.length - 1;
          return (
            <li key={b.path} className="flex items-center gap-1.5">
              {last ? (
                <span aria-current="page" className="font-medium text-foreground">
                  {b.label}
                </span>
              ) : (
                <a href={b.path} className="hover:text-foreground">
                  {b.label}
                </a>
              )}
              {!last && <span aria-hidden="true">/</span>}
            </li>
          );
        })}
      </ol>
      {emitSchema && (
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema(items)) }}
        />
      )}
    </nav>
  );
}
